'use client';

/**
 * Typed helpers for talking to the PopshopMobile React Native WebView shell.
 *
 * When popstore-next is the top-level document inside the native app, the shell
 * exposes `window.ReactNativeWebView` and communicates in two directions:
 *   - Web → Native: `window.ReactNativeWebView.postMessage(JSON.stringify(msg))`
 *   - Native → Web: browser `CustomEvent`s dispatched onto `window`
 *
 * This is intentionally tiny — just enough to type `window.ReactNativeWebView`
 * once and stop re-casting it at every call site (e.g. the Google Calendar
 * connect flow). Everything is a no-op on the normal web.
 *
 * SRP: this file owns ONLY native-specific work — talking to the shell. It does
 * NOT decide *whether* to talk to native and it has NO web fallback. The
 * "app user or not?" branch and the web behavior live at the creator call site,
 * so each site is free to pick its own web path (e.g. the Help nav item opens a
 * new tab, another site might soft-navigate). Gate with `getIsAppUser()` /
 * `useIsAppUser()` before calling anything here.
 */
export interface NativeBridgeMessage {
  type: string;
  [key: string]: unknown;
}

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (message: string) => void };
  }
}

/**
 * Post a message to the native shell. The `?.` is a defensive null-guard on the
 * raw bridge object (1:1 with PopshopRN's `window.ReactNativeWebView?.post…`) —
 * NOT the decision of whether to talk to native. Callers decide that with
 * `getIsAppUser()` / `useIsAppUser()` (the durable `appVersion` flag). No-op
 * when the shell isn't present.
 */
export function postToNative(message: NativeBridgeMessage): void {
  if (typeof window === 'undefined') return;
  window.ReactNativeWebView?.postMessage(JSON.stringify(message));
}

/**
 * Print a diagnostic in the NATIVE console (the shell's `debugLog` message), so
 * it lands in the Metro log.
 *
 * A WebView is a separate JS context with no pipe to the packager, so the
 * page's own `console.*` is invisible while debugging in a simulator — the only
 * way to read it is to attach Safari's Web Inspector to the WebView. This
 * forwards the line to the shell instead, which logs it natively.
 *
 * DIAGNOSTIC ONLY, and additive to the page's own `console` call rather than a
 * replacement — the web has no shell to forward to. Never pass a token, a
 * session cookie, an OAuth code or anything else that should not sit in a log:
 * Metro output is copied into tickets. `detail` must be JSON-serializable (an
 * `Error` is not — send its message and fields). No-op on the web, and ignored
 * by a shell too old to know the message.
 */
export function nativeDebugLog(tag: string, detail?: unknown): void {
  postToNative({ type: 'debugLog', tag, detail });
}

/**
 * Ask the native shell to open a URL in the system browser (its `openUrl`
 * message). Native-only — `window.open` is unreliable inside a WebView, so this
 * is the ONLY reliable way for an app user to reach an external page.
 *
 * The caller is responsible for gating this on `getIsAppUser()` and for
 * providing the plain-web behavior in the `else` branch (usually a new tab).
 */
export function nativeOpenUrl(url: string): void {
  if (!url) return;
  postToNative({ type: 'openUrl', url });
}

/**
 * Tell the native shell what colour the page paints its TOP STRIP, as
 * `#rrggbb` (its `headerColorChanged` message).
 *
 * The shell runs edge-to-edge — the WebView fills the window, notch included,
 * and the page pads itself — so this is not about painting the strip: the page
 * already does that. It is about the STATUS BAR DRAWN OVER IT. The shell picks
 * `dark-content` / `light-content` from this colour's luminance
 * (`statusBarStyleFor`), and its default is `#000000`, i.e. a WHITE clock and
 * battery. On a light page that is white on near-white — the clock is a ghost
 * over the page's own top row, which is what the notch strip looked like on the
 * organic echo home before this existed. It is also the colour the shell paints
 * natively in the moments the page has not: initial load, and an over-scroll
 * bounce past the top edge.
 *
 * Any surface owning a ground the status bar has to read against announces it
 * and puts it back when it goes; the caller gates on `getIsAppUser()`.
 */
export function nativeSetHeaderColor(headerColor: string): void {
  postToNative({ type: 'headerColorChanged', headerColor });
}

/**
 * Ask the native shell to open the OS share sheet (its `share` message). The
 * shell's `useShare` switches on `payload.action`; `'pdp'` is its generic
 * URL+title share mapper, so we always send that shape.
 *
 * Returns whether the message was actually posted, so a caller can fall back to
 * the web share modal when the shell isn't reachable (a stale persisted
 * `appVersion` outside the WebView, an older shell build). This is the ONE
 * helper here that reports back — a share must never silently vanish.
 *
 * `message` is what Android's share sheet actually sends (it ignores `url`), so
 * callers should fold the URL into it; omit it and the shell builds
 * `"<title>:\n<url>"` itself.
 */
export function nativeShare(input: {
  url: string;
  title?: string;
  message?: string;
}): boolean {
  if (!input?.url) return false;
  if (typeof window === 'undefined' || !window.ReactNativeWebView) return false;
  postToNative({
    type: 'share',
    payload: {
      action: 'pdp',
      url: input.url,
      title: input.title,
      message: input.message,
    },
  });
  return true;
}

/**
 * Ask the native shell to open `url` in its dedicated Apple Pay checkout WebView
 * (its `openApplePayCheckout` message).
 *
 * Apple Pay on the web needs `window.ApplePaySession`, which only exists on a
 * WebView carrying react-native-webview's `enableApplePay`. That prop disables
 * `injectedJavaScript`, `injectJavaScript` and `sharedCookiesEnabled` on the
 * WebView it is set on, so it can never be the one rendering this page — the
 * shell opens a SECOND WebView instead. The URL must be absolute and
 * same-origin; the shell rejects anything else, because this is a payment surface.
 *
 * Reports whether the message was posted so the caller can fall back to the
 * in-WebView checkout on a shell too old to know it (same contract as
 * {@link nativeShare} — a checkout must never silently vanish).
 */
export function nativeOpenApplePayCheckout(url: string): boolean {
  if (!url) return false;
  if (typeof window === 'undefined' || !window.ReactNativeWebView) return false;
  postToNative({ type: 'openApplePayCheckout', url });
  return true;
}

/** `appCheckoutResult` — the Apple Pay checkout modal closed. */
export type AppCheckoutResultDetail = {
  status: 'success' | 'cancelled' | 'failed';
};

/**
 * Ask the iOS shell to present the system Sign in with Apple sheet.
 *
 * Only the HASH of the nonce crosses — the raw value stays here, where the
 * Firebase credential is built. `requestId` is echoed back so a late reply
 * from an abandoned attempt cannot resolve a newer one. Reports whether the
 * message was posted, so an older shell falls back instead of dead-ending.
 */
export function nativeAppleSignIn(input: {
  requestId: string;
  hashedNonce: string;
}): boolean {
  if (!input?.requestId || !input.hashedNonce) return false;
  if (typeof window === 'undefined' || !window.ReactNativeWebView) return false;
  postToNative({
    type: 'requestAppleSignIn',
    requestId: input.requestId,
    hashedNonce: input.hashedNonce,
  });
  return true;
}

/**
 * `appAppleSignInResult` — the outcome of one {@link nativeAppleSignIn}.
 *
 * `fullName` and `email` come back on the FIRST authorization only, so the
 * name must be captured here or lost (the identity token still carries an
 * `email` claim every time). `authorizationCode` is kept for the revoke that
 * Apple requires on account deletion.
 */
export type AppAppleSignInResultDetail = {
  requestId: string;
  status: 'success' | 'cancelled' | 'failed';
  identityToken?: string;
  authorizationCode?: string;
  fullName?: string;
  email?: string;
  error?: string;
};

/**
 * StoreKit in-app subscriptions, native-only.
 *
 * An in-app subscription purchase MUST go through StoreKit (Guideline 3.1.1),
 * and StoreKit is unreachable from a WebView — so the plan picker stays here and
 * only the catalog read + purchase sheet cross into the shell. Each helper
 * reports whether the message was posted, so a caller on a shell too old to
 * know it can fall back to the "finish on the web" hand-off rather than leaving
 * a dead button. See `PopshopMobile/src/hooks/useIapSubscriptions.ts`.
 */
export function nativeIapFetchProducts(productIds: string[]): boolean {
  if (!productIds?.length) return false;
  if (typeof window === 'undefined' || !window.ReactNativeWebView) return false;
  postToNative({ type: 'iapFetchProducts', productIds });
  return true;
}

/**
 * Present Apple's purchase sheet for `productId`.
 *
 * `appAccountToken` must be a UUID (Apple rejects anything else, and the shell
 * drops a non-UUID rather than failing the purchase). It is what lets the
 * backend tie a renewal Apple reports later back to this creator, so pass one
 * whenever a stable per-creator UUID is available.
 */
export function nativeIapPurchase(input: {
  productId: string;
  appAccountToken?: string;
}): boolean {
  if (!input?.productId) return false;
  if (typeof window === 'undefined' || !window.ReactNativeWebView) return false;
  postToNative({
    type: 'iapPurchase',
    productId: input.productId,
    appAccountToken: input.appAccountToken,
  });
  return true;
}

/**
 * Tell the shell the purchase is safely granted, so StoreKit can finish the
 * transaction.
 *
 * Call this ONLY after the apiserver has verified the receipt and granted
 * entitlement. Until it is called the transaction stays unfinished on purpose:
 * Apple re-delivers an unfinished transaction, which is the only thing that
 * rescues a purchase whose verification failed. Calling it early takes the
 * creator's money and loses the grant.
 */
export function nativeIapFinishTransaction(transactionId: string): boolean {
  if (!transactionId) return false;
  if (typeof window === 'undefined' || !window.ReactNativeWebView) return false;
  postToNative({ type: 'iapFinishTransaction', transactionId });
  return true;
}

/** "Restore purchases" — required by Apple for a subscription app. */
export function nativeIapRestorePurchases(): boolean {
  if (typeof window === 'undefined' || !window.ReactNativeWebView) return false;
  postToNative({ type: 'iapRestorePurchases' });
  return true;
}

/** `appIapProducts` — StoreKit's catalog for the viewer's storefront. */
export type AppIapProductsDetail = {
  products: {
    id: string;
    /** Apple's localized price string. Display THIS, never our own amount. */
    displayPrice: string;
    currency?: string;
    title?: string;
  }[];
  error?: string;
};

/** `appIapPurchaseResult` — one per sheet; `cancelled` is not an error. */
export type AppIapPurchaseResultDetail = {
  status: 'success' | 'cancelled' | 'failed';
  /** Present on success — pass back to {@link nativeIapFinishTransaction}. */
  transactionId?: string;
  productId?: string;
  /** The StoreKit 2 JWS the apiserver verifies with Apple. */
  purchaseToken?: string;
  code?: string | null;
  message?: string | null;
};

/** `appIapRestoreResult` — what Apple says this Apple ID currently owns. */
export type AppIapRestoreResultDetail = {
  status: 'success' | 'failed';
  activeSubscriptions?: { productId: string; isActive: boolean }[];
};

/**
 * Ask the native shell to download a remote file (any type) to the device — it
 * fetches the URL and saves via the Storage Access Framework (Android) / Files
 * sheet (iOS). Native-only: a `blob:`/anchor download is unreachable in a
 * WebView, so app users MUST route through here. The caller gates on
 * `getIsAppUser()` and keeps its own `fetch`→blob→anchor path for the web.
 *
 * `filename` should carry the extension; `mimeType` (e.g. `image/jpeg`,
 * `video/mp4`, `application/zip`) lets the shell advertise the correct UTI.
 *
 * Wire message: the SHIPPED shell only exposes the (already-generic) URL-file
 * saver behind the legacy `downloadPdf` message — it downloads any type by
 * `{url, filename, mimeType}`, the name is the only PDF-specific thing about it.
 * We reuse it so this ships with ZERO mobile-side changes. FUTURE MOBILE
 * OPTIMIZATION: once a mobile release adds a correctly-named generic
 * `downloadFile` message (see the tracker's Migration Guards), switch the
 * `type` below from `'downloadPdf'` to `'downloadFile'` — one-line change here.
 */
export function nativeDownloadFile(
  url: string,
  filename?: string,
  mimeType?: string,
): void {
  if (!url) return;
  // Omit `filename` when unknown (e.g. only in Content-Disposition, which the
  // web path reads after fetching) — the shell infers it from the URL path.
  postToNative({ type: 'downloadPdf', payload: { url, filename, mimeType } });
}

/**
 * Save CLIENT-GENERATED file bytes to the device — the content-based sibling of
 * {@link nativeDownloadFile} (which fetches a remote URL). Used when the web app
 * built the file in memory (a `data:` URI / Blob → PDF, PNG, CSV, …) and there
 * is no URL to fetch. A `blob:`/anchor download is unreachable inside a WebView,
 * so app users MUST route the raw bytes through here; the shell writes them and
 * hands the file to the SAF (Android) / Files sheet (iOS).
 *
 * `content` is base64 for binary (PDF/PNG) or raw text for `utf8`; pass
 * `filename` WITH its extension and the real `mimeType` so the file saves
 * correctly named and typed.
 *
 * Wire message: the `downloadFile` message. NOTE this requires a mobile shell
 * that handles it (PopshopMobile's `handleFileContentDownload`); the older
 * URL-only `downloadPdf` handler cannot accept inline content. Gate the caller
 * on `getIsAppUser()` and keep a web (anchor/Blob-URL) path in the `else`.
 */
export function nativeDownloadFileContent(payload: {
  content: string;
  encoding?: 'base64' | 'utf8';
  filename?: string;
  mimeType?: string;
}): void {
  if (!payload?.content) return;
  postToNative({ type: 'downloadFile', payload });
}

/**
 * Session/device payload the shell injects via the `appSessionUpdate`
 * CustomEvent. NOTE: the shell interpolates every field into a string template,
 * so primitives arrive as strings (e.g. `newSession` is `'true'`/`'false'`, not
 * a boolean) — parse defensively.
 */
export interface AppSessionDetail {
  userId: string;
  sessionId: string;
  newSession: boolean | string;
  platform: string;
  deviceId: string;
  pushToken: string;
  deviceModel: string;
  lastUsedAppId: string;
  lastUsedAppVersion: string;
  userAgent: string;
}

/** Push payload the shell forwards via the `appNotificationResult` CustomEvent. */
export interface AppNotificationDetail {
  notification?: { title?: string; body?: string } | null;
  data?: {
    slug?: string;
    /**
     * Admin campaign this push came from (`customNotifications/{id}`). Set by
     * the backend's campaign send path.
     */
    campaignId?: string;
    /**
     * Push Builder template this push was rendered from. Set by the backend's
     * event-driven send path, which has no campaign to point at.
     */
    templateKey?: string;
    [key: string]: unknown;
  } | null;
}

/**
 * Tell the backend a push was opened.
 *
 * Fire-and-forget on purpose: this runs in the same handler that navigates to
 * the notification's destination, and a creator must never wait on — or lose a
 * navigation to — an analytics call. Failures are logged and dropped.
 *
 * `keepalive` matters more here than it looks: the very next statement in the
 * caller is a `window.location.assign`, which tears down the document. Without
 * it the browser is free to cancel the in-flight request, and cold-start taps —
 * the ones that navigate immediately — would be exactly the ones never counted.
 */
export async function reportPushClick(
  apiEndpoint: string,
  token: string,
  userId: string,
  payload: { campaignId?: string; templateKey?: string; sessionId?: string },
): Promise<void> {
  if (!payload.campaignId && !payload.templateKey) return;
  try {
    await fetch(
      `${apiEndpoint}/v1/users/${encodeURIComponent(userId)}/push-notification-clicks`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        keepalive: true,
      },
    );
  } catch (error) {
    console.warn('[native-bridge] push click report failed', error);
  }
}
